/**
 * i18next runtime — 20 languages, Hebrew default, English fallback.
 *
 * Bundle layout: `src/locales/<code>/<namespace>.json`. Each namespace file
 * is merged into ONE i18next namespace ("translation") under the key prefix
 * `<namespace>.` — so a component calls `t("cameras.addTitle")` and the file
 * `locales/he/cameras.json` holds `{ "addTitle": "..." }`.
 *
 * `shared.json` is one of those namespaces but is GENERATED, not authored:
 * `scripts/i18n/sync_shared.py` mirrors `backend/i18n/<code>.json` into it, so
 * the strings the backend also sends (alert-type labels, zone modes,
 * severities, push text) have a single authored copy. Do not edit it here, and
 * do not go back to globbing `../../../backend/i18n/*.json` directly: that
 * resolves locally and matches NOTHING on Vercel, which is handed the `web/`
 * directory alone — silently, so production renders raw keys like
 * "shared.severity.critical". `backend/tests/test_i18n_bundle.py` guards the
 * mirror against drift.
 *
 * Hebrew + English are bundled eagerly (first paint + fallback); the other 18
 * are code-split and fetched on demand the first time they are selected.
 */
import i18next, { type Resource } from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import {
  DEFAULT_LANGUAGE,
  FALLBACK_LANGUAGES,
  LANGUAGE_CODES,
  LANGUAGE_STORAGE_KEY,
  dirFor,
  normalizeLanguage,
} from "./languages";
import { setFormatLanguage } from "./format";

type Json = Record<string, unknown>;

/** `{ "a": { "b": "x" } }` + ns → `{ "ns.a.b": "x" }` (test / check helper). */
export function flattenKeys(obj: Json, prefix: string): Record<string, string> {
  const out: Record<string, string> = {};
  const walk = (node: unknown, path: string) => {
    if (node && typeof node === "object" && !Array.isArray(node)) {
      for (const [k, v] of Object.entries(node as Json)) walk(v, path ? `${path}.${k}` : k);
    } else {
      out[path] = String(node ?? "");
    }
  };
  walk(obj, prefix);
  return out;
}

/** Sorted, de-duplicated `{{placeholder}}` names inside a string. */
export function placeholdersOf(s: string): string[] {
  const found = new Set<string>();
  for (const m of s.matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)) found.add(m[1]);
  return [...found].sort();
}

/**
 * Turn a Vite glob map (`"../locales/<code>/<ns>.json" → json`) into an
 * i18next `resources` object: `{ <code>: { translation: { <ns>: json } } }`.
 */
export function buildResources(files: Record<string, Json>): Record<string, { translation: Json }> {
  const res: Record<string, { translation: Json }> = {};
  for (const [file, json] of Object.entries(files)) {
    const m = /\/([a-z]{2})\/([A-Za-z0-9_-]+)\.json$/.exec(file);
    if (!m) continue;
    const [, code, ns] = m;
    res[code] ??= { translation: {} };
    // The glob imports each file with `import: "default"`, so `json` is already
    // the bundle. Never unwrap a `default` key here: checklists.json has a
    // real top-level "default" list and unwrapping it emptied every checklist.
    res[code].translation[ns] = json;
  }
  return res;
}

// ── eager: he + en (default + fallback) ────────────────────────────────
const eagerWeb = import.meta.glob<Json>("../locales/{he,en}/*.json", { eager: true, import: "default" });

// ── lazy: everything else ──────────────────────────────────────────────
const lazyWeb = import.meta.glob<Json>("../locales/*/*.json", { import: "default" });

const resources: Resource = buildResources(eagerWeb);

const loaded = new Set<string>(Object.keys(resources));

/** Fetch + register the bundle for `code` (no-op for he/en and repeats). */
export async function ensureLanguageLoaded(code: string): Promise<void> {
  if (loaded.has(code)) return;
  const bundle: Json = {};
  const webFiles = Object.entries(lazyWeb).filter(([f]) => f.includes(`/locales/${code}/`));
  await Promise.all(
    webFiles.map(async ([file, loader]) => {
      const m = /\/([A-Za-z0-9_-]+)\.json$/.exec(file);
      if (m) bundle[m[1]] = await loader();
    }),
  );
  i18next.addResourceBundle(code, "translation", bundle, true, true);
  loaded.add(code);
}

export function applyDocumentLanguage(code: string): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = code;
  document.documentElement.dir = dirFor(code);
}

/**
 * Called when a locale bundle cannot be fetched. Installed by main.tsx so
 * this module stays free of React/zustand imports (it runs before render).
 * A missing locale chunk means the served build moved on without this tab.
 */
let onLazyLoadFailure: (() => void) | undefined;
export function setLazyLoadFailureHandler(fn: () => void): void {
  onLazyLoadFailure = fn;
}

let initialized = false;

export function initI18n(): typeof i18next {
  if (initialized) return i18next;
  initialized = true;

  const detector = new LanguageDetector(null, {
    order: ["localStorage", "navigator"],
    lookupLocalStorage: LANGUAGE_STORAGE_KEY,
    caches: [], // we persist explicitly in changeLanguage()
    convertDetectedLanguage: (lng: string) => normalizeLanguage(lng),
  });

  void i18next
    .use(detector)
    .use(initReactI18next)
    .init({
      resources,
      supportedLngs: [...LANGUAGE_CODES],
      fallbackLng: [...FALLBACK_LANGUAGES],
      lng: undefined, // let the detector decide
      load: "languageOnly",
      nonExplicitSupportedLngs: true,
      ns: ["translation"],
      defaultNS: "translation",
      keySeparator: ".",
      nsSeparator: false,
      interpolation: { escapeValue: false },
      returnNull: false,
      react: { useSuspense: false },
    });

  const sync = (lng: string) => {
    const code = normalizeLanguage(lng);
    setFormatLanguage(code);
    applyDocumentLanguage(code);
  };
  sync(i18next.resolvedLanguage ?? i18next.language ?? DEFAULT_LANGUAGE);
  i18next.on("languageChanged", sync);
  // If the detector picked a lazy language (e.g. returning "ru" user), load it now.
  const initial = normalizeLanguage(i18next.language);
  // A returning user whose language is lazy. If its chunk is gone (this tab
  // predates a deploy) we stay on the fallback rather than crash the boot —
  // the stale-build banner is what tells them why.
  if (!loaded.has(initial)) {
    void ensureLanguageLoaded(initial)
      .then(() => i18next.changeLanguage(initial))
      .catch(() => onLazyLoadFailure?.());
  }
  return i18next;
}

/**
 * The one entry point the UI uses to switch language. Loads the bundle,
 * flips i18next, persists to localStorage. The caller (LanguageSwitcher)
 * additionally PATCHes /api/auth/me when a user is logged in.
 */
export async function changeLanguage(code: string): Promise<void> {
  const normalized = normalizeLanguage(code);
  await ensureLanguageLoaded(normalized);
  await i18next.changeLanguage(normalized);
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
  } catch {
    /* private mode / quota — the in-memory language still applies */
  }
}

export function currentLanguage(): string {
  return normalizeLanguage(i18next.resolvedLanguage ?? i18next.language ?? DEFAULT_LANGUAGE);
}

export { i18next };
export default i18next;
